/**
 * Step1DocDirection — sélection humaine explicite de la direction commerciale
 * (9.T.5.v3.6, QA §1).
 *
 * QA §1 v3.6 : pré-sélection UNIQUEMENT si une suggestion réelle est disponible
 * (`suggested` non-null). Le broker doit toujours confirmer explicitement.
 * Aucune dérivation automatique depuis `classification.kind`.
 */

import type { DocDirection } from "@kollectivia/shared";
import { useTranslation } from "react-i18next";

const OPTIONS: DocDirection[] = ["advisor_document", "insurer_response", "client_input", "unknown"];

type Props = {
  value: DocDirection | undefined;
  onChange: (v: DocDirection) => void;
  suggested: DocDirection | null;
};

export function Step1DocDirection({ value, onChange, suggested }: Props) {
  const { t } = useTranslation();
  return (
    <section>
      <h3 className="mb-1 text-lg font-medium text-slate-900">
        {t("intake_events.detail.materialize.wizard.step_1_doc_direction.title")}
      </h3>
      <p className="mb-4 text-sm text-slate-600">
        {t("intake_events.detail.materialize.wizard.step_1_doc_direction.subtitle")}
      </p>
      {suggested && (
        <p className="mb-3 text-xs text-slate-500" aria-live="polite">
          {t("intake_events.detail.materialize.wizard.step_1_doc_direction.suggested_hint", {
            value: t(
              `intake_events.detail.materialize.wizard.step_1_doc_direction.options.${suggested}`,
            ),
          })}
        </p>
      )}
      <div role="radiogroup" className="space-y-2">
        {OPTIONS.map((opt) => (
          <label
            key={opt}
            className="focus-within:ring-brand-500 flex cursor-pointer items-start gap-3 rounded-md border border-slate-200 p-3 focus-within:ring-2 hover:bg-slate-50"
          >
            <input
              type="radio"
              name="doc-direction"
              value={opt}
              checked={value === opt}
              onChange={() => onChange(opt)}
              className="focus-visible:ring-brand-500 mt-1 focus-visible:ring-2"
            />
            <span className="text-sm text-slate-800">
              {t(`intake_events.detail.materialize.wizard.step_1_doc_direction.options.${opt}`)}
            </span>
          </label>
        ))}
      </div>
    </section>
  );
}
